import { OPERATORS, RULE_OPERATORS, type QuestionType } from '@cyberscore/contracts';

/**
 * The visual condition editor's logic (task 43). One shape serves question visibility, stage entry,
 * workflow branches (the question engine's operators) and rules (which add date and provenance tests).
 *
 *   leaf  { question | fact, op, value?, provenance? }
 *   group { all: [...] } · { any: [...] } · { not: {...} }
 */

export type Mode = 'question' | 'rule';
export type Leaf = { question?: string; fact?: string; op: string; value?: unknown; provenance?: string[] };
export type Cond = Leaf | { all: Cond[] } | { any: Cond[] } | { not: Cond };

export interface QuestionInfo {
  key: string;
  text: string;
  type: QuestionType | string;
  options: { value: string; label: string }[];
}

export const OP_LABEL: Record<string, string> = {
  eq: 'is',
  ne: 'is not',
  in: 'is one of',
  not_in: 'is none of',
  gt: 'is more than',
  gte: 'is at least',
  lt: 'is less than',
  lte: 'is at most',
  between: 'is between',
  contains: 'includes',
  answered: 'is answered',
  not_answered: 'is not answered',
  expired: 'is in the past',
  older_than: 'is older than',
  within_last: 'is within the last',
  within_next: 'is within the next',
  verified: 'is verified',
};

const NO_VALUE = new Set(['answered', 'not_answered', 'expired', 'verified']);
const LIST = new Set(['in', 'not_in']);
const DURATION = new Set(['older_than', 'within_last', 'within_next']);
const CHOICE_TYPES = new Set([
  'single_choice',
  'multiple_choice',
  'boolean',
  'entity_selector',
  'asset_selector',
]);
const NUMBER_TYPES = new Set(['numeric', 'currency', 'percentage', 'employee_count', 'rating']);
const DATE_TYPES = new Set(['date', 'date_range']);

export const needsValue = (op: string) => !NO_VALUE.has(op);
export const takesList = (op: string) => LIST.has(op);
export const takesDuration = (op: string) => DURATION.has(op);

/** The operators that make sense for a question of this type (or a fact), in the editor's order. */
export function opsFor(type: string | null, mode: Mode): string[] {
  const all: readonly string[] = mode === 'rule' ? RULE_OPERATORS : OPERATORS;
  const allowed = (ops: string[]) => ops.filter((o) => all.includes(o));
  const base = ['answered', 'not_answered', ...(mode === 'rule' ? ['verified'] : [])];
  if (type === null) return allowed(['eq', 'ne', 'in', 'not_in', 'gt', 'gte', 'lt', 'lte', 'contains', 'answered', 'not_answered']); // prettier-ignore
  if (CHOICE_TYPES.has(type)) return allowed(['eq', 'ne', 'in', 'not_in', ...(type === 'multiple_choice' || type === 'asset_selector' ? ['contains'] : []), ...base]); // prettier-ignore
  if (NUMBER_TYPES.has(type)) return allowed(['gt', 'gte', 'lt', 'lte', 'between', 'eq', 'ne', ...base]);
  if (DATE_TYPES.has(type)) return allowed(['expired', 'within_next', 'within_last', 'older_than', 'gt', 'lt', 'between', ...base]); // prettier-ignore
  return allowed(['eq', 'ne', 'contains', ...base]);
}

/** A sensible value when the operator (or subject) changes, keeping what still fits. */
export function valueFor(op: string, q: QuestionInfo | undefined, current: unknown): unknown {
  if (!needsValue(op)) return undefined;
  if (takesDuration(op)) return typeof current === 'string' && /^\d+[dwmy]$/.test(current) ? current : '90d';
  if (takesList(op)) {
    const list = Array.isArray(current) ? current : current === undefined || current === '' ? [] : [current];
    return list.length ? list : q?.options[0] ? [q.options[0].value] : [];
  }
  if (op === 'between') return Array.isArray(current) && current.length === 2 ? current : [0, 0];
  if (Array.isArray(current)) return current[0] ?? q?.options[0]?.value ?? '';
  if (current !== undefined && current !== '') return current;
  return q?.options[0]?.value ?? (q && NUMBER_TYPES.has(q.type) ? 0 : '');
}

export function newLeaf(q: QuestionInfo | undefined, mode: Mode): Leaf {
  const op = opsFor(q?.type ?? 'text', mode)[0]!;
  return { question: q?.key ?? '', op, ...(needsValue(op) ? { value: valueFor(op, q, undefined) } : {}) };
}

export function isGroup(c: Cond): c is { all: Cond[] } | { any: Cond[] } {
  return 'all' in c || 'any' in c;
}

/** The leaf without some of its fields. */
export function omit<K extends keyof Leaf>(leaf: Leaf, ...keys: K[]): Omit<Leaf, K> {
  const out: Partial<Leaf> = { ...leaf };
  for (const k of keys) delete out[k];
  return out as Omit<Leaf, K>;
}

/** Sets a leaf's operator, fixing its value to suit. */
export function withOp(leaf: Leaf, op: string, q: QuestionInfo | undefined): Leaf {
  const rest = omit(leaf, 'value');
  const value = valueFor(op, q, leaf.value);
  return value === undefined ? { ...rest, op } : { ...rest, op, value };
}

/** Points a leaf at another question (or fact), keeping the operator if it still makes sense. */
export function withSubject(
  leaf: Leaf,
  subject: { question: string } | { fact: string },
  q: QuestionInfo | undefined,
  mode: Mode,
): Leaf {
  const ops = opsFor('fact' in subject ? null : (q?.type ?? 'text'), mode);
  const op = ops.includes(leaf.op) ? leaf.op : ops[0]!;
  const { provenance } = leaf;
  const rest = omit(leaf, 'question', 'fact', 'value', 'provenance');
  const value = valueFor(op, q, 'fact' in subject ? leaf.value : undefined);
  return { ...rest, ...subject, op, ...(value === undefined ? {} : { value }), ...(provenance && 'question' in subject ? { provenance } : {}) }; // prettier-ignore
}

/** Plain words for a condition, e.g. “Q044 (How do vendors…) is Always-on connection, and industry is manufacturing”. */
export function describe(c: Cond | undefined, questions: ReadonlyMap<string, QuestionInfo>): string {
  if (!c) return 'Always';
  if ('all' in c) return c.all.map((x) => wrap(x, questions)).join(' and ');
  if ('any' in c) return c.any.map((x) => wrap(x, questions)).join(' or ');
  if ('not' in c) return `not (${describe(c.not, questions)})`;
  const q = c.question ? questions.get(c.question) : undefined;
  const label = (v: unknown) => q?.options.find((o) => o.value === v)?.label ?? String(v);
  const value = !needsValue(c.op)
    ? ''
    : Array.isArray(c.value)
      ? ` ${c.op === 'between' ? c.value.map(label).join(' and ') : c.value.map(label).join(', ')}`
      : ` ${label(c.value)}`;
  const from = c.provenance?.length
    ? ` (from ${c.provenance.join(' or ').toLowerCase().replace(/_/g, ' ')})`
    : '';
  return `${c.question ?? c.fact} ${OP_LABEL[c.op] ?? c.op}${value}${from}`;
}
const wrap = (c: Cond, q: ReadonlyMap<string, QuestionInfo>) =>
  isGroup(c) ? `(${describe(c, q)})` : describe(c, q);

/** Replaces the node at `path` (indexes into all/any lists; 'not' steps into a negation). */
export function update(c: Cond, path: (number | 'not')[], f: (x: Cond) => Cond | null): Cond | null {
  if (!path.length) return f(c);
  const [head, ...rest] = path;
  if (head === 'not') {
    if (!('not' in c)) return c;
    const inner = update(c.not, rest, f);
    return inner ? { not: inner } : null;
  }
  const key = 'all' in c ? 'all' : 'any' in c ? 'any' : null;
  if (!key) return c;
  const list = (c as Record<string, Cond[]>)[key]!;
  const next = list.flatMap((x, i) => (i === head ? [update(x, rest, f)].filter((y): y is Cond => y !== null) : [x])); // prettier-ignore
  // An empty group goes away; a group of one stays (the person may be adding to it).
  return next.length ? ({ [key]: next } as Cond) : null;
}
